<template>
  <div class="ticket-page">
    <div class="page-wrapper">
      <BackLink
        :text="$t('afterSale.rebook.backToTickets')"
        :route-to="{ name: 'MyBookings' }"
      />

      <p class="ticket-page__booking-number">
        {{ $t('booking.bookingNumber') }}:
        <strong>{{ bookingNumber }}</strong>
      </p>

      <section class="grid-layout">
        <div class="grid-layout__main">
          <div class="ticket-page__shout-out">
            <MyPageShoutOut />
          </div>
          <template v-if="errorStore.errorMessage">
            <div class="error-message">
              {{ errorStore.errorMessage }}
            </div>
          </template>
          <template v-else-if="loading">
            <div class="loading">
              {{ $t('common.status.loading') }}
            </div>
          </template>
          <template v-else>
            <div class="ticket-page__tickets">
              <div
                v-if="outboundBookings.length"
                class="ticket-page__segment"
              >
                <h2
                  v-if="hasInboundBooking && outboundBookings.length === 1"
                  class="ticket-page__segment-title"
                >
                  {{ $t('booking.outbound') }}
                </h2>

                <div
                  v-for="(segment, index) in outboundBookings"
                  :key="segment.id"
                  class="ticket-page__segment-container"
                >
                  <Ticket
                    :bookingSegment="segment"
                    :customerName="customerName"
                    :passengers="outboundPassengers"
                    :totalPricePaid="totalPricePaid"
                    :segmentPrice="
                      [
                        ...(segment.required_products || []),
                        ...(segment.additional_products || []),
                      ].reduce((sum, product) => sum + (product.price || 0), 0)
                    "
                    :vouchers="vouchers"
                    :qrCodes="qrCodes"
                    :isTransferSegment="index > 0"
                    :transferInfo="
                      index > 0
                        ? getTransferInfo(outboundBookings[index - 1], segment)
                        : null
                    "
                  />
                </div>
              </div>

              <div
                v-if="inboundBookings.length"
                class="ticket-page__segment"
              >
                <h2
                  v-if="hasInboundBooking && inboundBookings.length === 1"
                  class="ticket-page__segment-title"
                >
                  {{ $t('booking.inbound') }}
                </h2>

                <div
                  v-for="(segment, index) in inboundBookings"
                  :key="segment.id"
                  class="ticket-page__segment-container"
                >
                  <Ticket
                    :bookingSegment="segment"
                    :customerName="customerName"
                    :passengers="inboundPassengers"
                    :totalPricePaid="totalPricePaid"
                    :segmentPrice="
                      [
                        ...(segment.required_products || []),
                        ...(segment.additional_products || []),
                      ].reduce((sum, product) => sum + (product.price || 0), 0)
                    "
                    :vouchers="vouchers"
                    :qrCodes="qrCodes"
                    :isTransferSegment="index > 0"
                    :transferInfo="
                      index > 0
                        ? getTransferInfo(inboundBookings[index - 1], segment)
                        : null
                    "
                  />
                </div>
              </div>
            </div>
            <div
              v-if="qrCodes.length"
              class="ticket-page__qr-codes"
            >
              <h2>{{ $t('myPage.ticket.displayQr') }}</h2>
              <div class="ticket-page__qr-list">
                <div
                  v-for="(qr, index) in qrCodes"
                  :key="index"
                  class="ticket-page__qr-item"
                >
                  <img
                    :src="qr.url"
                    :alt="$t('myPage.ticket.displayQr', { number: index + 1 })"
                    class="ticket-page__qr-image"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="grid-layout__sidebar">
          <!-- <AddOnLinks
            v-if="booking"
            :bookingData="booking"
            :booking-number="bookingNumber"
          /> -->
          <EditBookingLinks
            v-if="booking"
            :booking-number="bookingNumber"
            :can-be-rebooked="canRebook"
            :can-be-cancelled="canCancel"
            :can-change-name="canChangeName"
            class="card"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import { fetchBookingData, revertBooking } from '@/services/journeyService'
  import MyPageShoutOut from '@/components/MyPage/MyPageShoutOut.vue'
  import BackLink from '@/components/BaseComponents/BackLink.vue'
  import Ticket from '@/components/TravelPlanner/Ticket/Ticket.vue'
  import AddOnLinks from '@/components/MyPage/AddOnLinks.vue'
  import EditBookingLinks from '@/components/MyPage/EditBookingLinks.vue'
  import arrow from '@/assets/icons/arrow.svg'
  import { formatTime } from '@/utils/dateFormatter'

  export default defineComponent({
    name: 'TicketPage',
    components: {
      BackLink,
      Ticket,
      AddOnLinks,
      EditBookingLinks,
      MyPageShoutOut,
    },
    setup() {
      const route = useRoute()
      const i18n = useI18n()
      const bookingStore = useBookingStore()
      const errorStore = useErrorStore()
      const authStore = useAuthStore()
      const seatReservationStore = useSeatReservationStore()

      const loading = ref(true)
      const bookingNumber = computed(() => route.params.bookingNumber as string)
      const booking = computed(() => bookingStore.bookingData)

      const outboundBookings = computed(() => {
        const segments = booking.value?.outbound_booking_tariff_segments || []
        return segments.filter(
          (segment) =>
            segment.required_products && segment.required_products.length > 0
        )
      })

      const inboundBookings = computed(() => {
        const segments = booking.value?.inbound_booking_tariff_segments || []
        return segments.filter(
          (segment) =>
            segment.required_products && segment.required_products.length > 0
        )
      })

      const outboundBooking = computed(() => {
        if (!booking.value?.outbound_booking_tariff_segments?.length)
          return null

        const activeSegment =
          booking.value.outbound_booking_tariff_segments.find((segment) =>
            segment.required_products?.some((product) => !product.cancelled)
          )

        return (
          activeSegment || booking.value.outbound_booking_tariff_segments[0]
        )
      })

      const inboundBooking = computed(() => {
        if (!booking.value?.inbound_booking_tariff_segments?.length) return null

        const activeSegment =
          booking.value.inbound_booking_tariff_segments.find((segment) =>
            segment.required_products?.some((product) => !product.cancelled)
          )

        return activeSegment || booking.value.inbound_booking_tariff_segments[0]
      })

      const hasInboundBooking = computed(() => inboundBookings.value.length > 0)

      const customerName = computed(() => {
        const customer = booking.value?.customer
        if (!customer) return ''
        return `${customer.first_name} ${customer.last_name}`
      })

      const outboundPassengers = computed(() => {
        if (!booking.value?.passengers) return []
        return booking.value.passengers
          .filter((passenger) => !passenger.cancel_timestamp)
          .map((passenger) => ({
            type: passenger.type,
            id: passenger.id,
            discount_cards: passenger.discount_cards,
          }))
      })

      const inboundPassengers = computed(() => {
        if (!booking.value?.passengers) return []
        return booking.value.passengers
          .filter((passenger) => !passenger.cancel_timestamp)
          .map((passenger) => ({
            type: passenger.type,
            id: passenger.id,
            discount_cards: passenger.discount_cards,
          }))
      })

      const totalPricePaid = computed(() => {
        if (!booking.value) return 0
        return booking.value.total_price_paid || 0
      })

      const vouchers = computed(() => {
        if (!booking.value?.vouchers) return []
        return booking.value.vouchers.map((voucher) => ({
          amount: voucher.amount || 0,
          currency: voucher.currency || 'NOK',
        }))
      })

      const qrCodes = computed(() => {
        if (!booking.value?.ticket_documents) return []
        return booking.value.ticket_documents
          .filter((doc) => doc.document_type === 'QR')
          .map((doc) => ({
            url: doc.url || '',
          }))
      })

      const canRebook = computed(() => {
        const outboundCanRebook =
          outboundBooking.value?.required_products?.some(
            (p) => p.can_be_rebooked
          ) ?? false
        const inboundCanRebook =
          inboundBooking.value?.required_products?.some(
            (p) => p.can_be_rebooked
          ) ?? false
        return outboundCanRebook || inboundCanRebook
      })

      const canCancel = computed(() => {
        const outboundCanCancel =
          outboundBooking.value?.required_products?.some(
            (p) => p.can_be_cancelled
          ) ?? false
        const inboundCanCancel =
          inboundBooking.value?.required_products?.some(
            (p) => p.can_be_cancelled
          ) ?? false
        return outboundCanCancel || inboundCanCancel
      })

      const canChangeName = computed(() => {
        const outboundCanChangeName =
          outboundBooking.value?.required_products?.some(
            (p) => p.can_change_name
          ) ?? false
        const inboundCanChangeName =
          inboundBooking.value?.required_products?.some(
            (p) => p.can_change_name
          ) ?? false
        return outboundCanChangeName || inboundCanChangeName
      })

      const loadBookingData = async () => {
        try {
          errorStore.clearError()
          loading.value = true

          const useMyPageMode =
            !authStore.isBookingToken ||
            authStore.currentBookingNumber !== bookingNumber.value

          const data = await fetchBookingData(
            bookingNumber.value,
            useMyPageMode
          )
          bookingStore.setBookingData(data.data.booking)

          const booking = data.data.booking
          if (
            booking &&
            booking.booking_number &&
            (booking.last_after_sales_operation === 'AFTERSALES_REBOOK' ||
              booking.outbound_booking_tariff_segments?.some((segment) =>
                segment.required_products?.some(
                  (product) => product.provisional
                )
              ) ||
              booking.inbound_booking_tariff_segments?.some((segment) =>
                segment.required_products?.some(
                  (product) => product.provisional
                )
              ))
          ) {
            try {
              await revertBooking(booking.booking_number)

              const updatedData = await fetchBookingData(
                bookingNumber.value,
                true
              )
              bookingStore.setBookingData(updatedData.data.booking)
            } catch (error) {
              console.error('Failed to revert provisional changes:', error)
            }
          }
        } catch (error) {
          console.error('Error loading booking data:', error)

          if (error.message === 'Kunne ikke finne bestillingen.') {
            errorStore.setError(i18n.t('myPage.ticket.bookingNotFound'))
          }
        } finally {
          loading.value = false
        }
      }

      onMounted(async () => {
        seatReservationStore.$reset()
        await loadBookingData()
      })

      const getTransferInfo = (previousSegment: any, currentSegment: any) => {
        const prevArrival = new Date(
          previousSegment.booking_journey_segments[0].arrival_date_time
        )
        const currentDeparture = new Date(
          currentSegment.booking_journey_segments[0].departure_date_time
        )

        return {
          location: currentSegment.departure_station.name,
          duration: Math.round(
            (currentDeparture.getTime() - prevArrival.getTime()) / 1000 / 60
          ),

          arrivalTime: formatTime(prevArrival.toISOString()),
          departureTime: formatTime(currentDeparture.toISOString()),
        }
      }

      const getTransferDuration = (currentSegment: any, nextSegment: any) => {
        const currentArrival = new Date(
          currentSegment.booking_journey_segments[0].arrival_date_time
        )
        const nextDeparture = new Date(
          nextSegment.booking_journey_segments[0].departure_date_time
        )
        const minutes = Math.round(
          (nextDeparture.getTime() - currentArrival.getTime()) / 1000 / 60
        )

        return `${minutes} ${i18n.t('booking.minutes')}`
      }

      return {
        loading,
        bookingNumber,
        booking,
        outboundBookings,
        inboundBookings,
        hasInboundBooking,
        customerName,
        outboundPassengers,
        inboundPassengers,
        totalPricePaid,
        vouchers,
        qrCodes,
        canRebook,
        canCancel,
        canChangeName,
        errorStore,
        getTransferInfo,
        getTransferDuration,
        arrow,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .ticket-page {
    &__shout-out {
      margin-bottom: 1rem;
    }

    &__qr-codes {
      margin-bottom: 1rem;
    }

    &__qr-list {
      display: flex;
      flex-wrap: wrap;
    }

    &__qr-image {
      width: 100%;
      height: auto;
    }

    &__actions {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-medium);
    }
  }

  .loading {
    text-align: center;
    padding: var(--spacing-large);
  }

  .error-message {
    color: var(--color-error);
    text-align: center;
    padding: var(--spacing-large);
  }
</style>
